<template>
    <div class="account-panel">
        <Panel>
            <h2>Account</h2>

            <TabList :tab-map="tabMap" :selected-key="selectedTab">
                <template #account>
                    <h4>{{ fullName }}</h4>

                    <div v-if="isAzureAdUser">Logged in through EMS|MC login (Azure AD)</div>

                    <Loader wait-text="Loading your data..." v-if="isLoading"/>
                    <div v-else>
                        <h5>{{ user.title }}</h5>

                        <div v-if="user.phone_number?.length > 0">Phone Number: {{ user.phone_number }}</div>
                        <div v-if="fullAddress.length > 0">Address: {{ fullAddress }}</div>
                    </div>

                    <div class="user-buttons">
                        <button class="btn btn-danger log-out-button" @click="logOut()">Log out</button>
                    </div>

                </template>
                <template #password_change>
                    <div v-if="isAzureAdUser">
                        <button class="btn btn-primary" @click="goToAzureAdPasswordReset()"><i class="fa fa-link"></i>
                            Go to EMS|MC Password Reset
                        </button>
                    </div>
                    <div v-else>
                        <Alert type="success" message="Your password was changed successfully."
                               v-if="isPasswordChangeSuccessful"/>

                        <PasswordChangeForm
                            ref="passwordChangeForm"
                            :error-message="errorMessage"
                            :current-password-required="true"
                            :is-processing="isLoading"
                            @change-password="handlePasswordChange"
                        />
                    </div>
                </template>
                <template #mfa_change>
                    <MfaChange :user="user"/>
                </template>
                <template #profile_photo>
                    <ProfilePhoto/>
                </template>
            </TabList>
        </Panel>
    </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";

import { getFullUserName, logOut, userState } from "@/stores/user";
import { useLoading } from "@/hooks/use-loading";
import { getData, resetPassword } from "@/api/user";
import { goToAzureAdPasswordReset, isAzureAdEmail } from "@/lib/azure";

import Panel from "@/components/Panel.vue";
import TabList from "@/components/TabList.vue";
import Loader from "@/components/Loader.vue";
import PasswordChangeForm from "@/pages/common/PasswordChangeForm.vue";
import Alert from "@/components/Alert.vue";
import MfaChange from "@/pages/user/AccountPanel/MfaChange.vue";
import ProfilePhoto from "@/pages/user/AccountPanel/ProfilePhoto.vue";

export default {
    name: "AccountPanel",
    components: { ProfilePhoto, MfaChange, Alert, PasswordChangeForm, Loader, TabList, Panel },
    setup() {
        const selectedTab = ref('account');
        const { isLoading, startLoading, stopLoading } = useLoading(false);

        const isPasswordChangeSuccessful = ref(false);

        const errorMessage = ref('');
        const passwordChangeForm = ref(null);

        const user = ref({
            first_name: '',
            last_name: '',
            title: '',
            address_street: '',
            address_city: '',
            address_state: '',
            address_zipcode: '',
            phone_number: '',
            mfa_type: '',
        });

        const fullAddress = computed(() => {
            const addressParts = [
                'address_street',
                'address_city',
                'address_state',
                'address_zipcode',
            ];

            const result = [];
            for (const part of addressParts) {
                if (user.value[part]?.length > 0) {
                    result.push(user.value[part]);
                }
            }

            return result.join(', ');
        });

        onMounted(async () => {
            startLoading();
            try {
                const { data } = await getData();
                user.value = data.result;
            } catch (e) {
                console.error(e);
            } finally {
                stopLoading();
            }

        });

        const isAzureAdUser = computed(() => isAzureAdEmail(userState.username));

        const handlePasswordChange = async ({ current_password, new_password }) => {
            errorMessage.value = '';
            isPasswordChangeSuccessful.value = false;
            startLoading();

            try {
                await resetPassword({
                    current_password,
                    new_password
                });

                passwordChangeForm.value.reset();
                isPasswordChangeSuccessful.value = true;
            } catch (e) {
                errorMessage.value = e?.response?.data?.message ?? 'Unknown error';
            }

            stopLoading();
        };

        return {
            user,
            passwordChangeForm,
            isPasswordChangeSuccessful,
            errorMessage,
            handlePasswordChange,
            goToAzureAdPasswordReset: goToAzureAdPasswordReset,
            logOut,
            isAzureAdUser,
            fullAddress,
            selectedTab,
            isLoading,
            tabMap: {
                account: 'Your account',
                password_change: 'Change Password',
                mfa_change: 'Multi-Factor Authentication',
                profile_photo: 'Profile Photo',
            },
            fullName: computed(() => getFullUserName())
        }
    }
}
</script>

<style scoped lang="scss">
.account-panel {
    .user-buttons {
        margin-top: 20px;
    }
}
</style>